import { useMemo } from 'react'
import type { Model } from '../../../data/models'
import type { GenerateSettings } from './useGenerateSettings'
import type { AudioOperation } from '../../../context/GenerateContext'
import { computeModelCost } from '../../../utils/modelCost'
import { durationLock } from '../utils/referenceLimits'

interface UseCostParams {
	selectedModel: Model | null
	settings: GenerateSettings
	requiresVideo: boolean
	selectedVideoDuration: number
	uploadedVideoDuration?: number
	/** Для аудио-моделей: текущая операция (generate / extend / cover /
	 *  add_vocals / add_instrumental). Определяет по какому variant-ключу
	 *  искать цену — иначе юзер видит цену базовой генерации при
	 *  параметрических операциях. */
	audioOperation?: AudioOperation
	/** Для presentation: динамическая оценка от `/presentations/estimate`.
	 *  Зависит от содержимого PPTX / темы / количества картинок — не вытаскивается
	 *  из variants. `null` когда estimate ещё не готов (показываем base cost модели). */
	presentationEstimate?: number | null
	hasImage?: boolean
	hasVideo?: boolean
	/** Сколько фото уйдёт в запрос — надбавка за каждое у моделей со строкой «input_photo». */
	imageCount?: number
	/** Видео-референсы в слотах: длину ролика может задавать видео (своё или тренда — utils/referenceLimits.durationLock). */
	referenceVideos?: { duration?: number; url?: string; cardRef?: boolean }[]
	/** Секунды видео на входе к оплате (плата за секунды входного видео, useInputVideoSeconds). */
	inputVideoSeconds?: number | null
}

/**
 * Hook-обёртка над `computeModelCost` (pure-функцией из utils/modelCost.ts).
 * Сам расчёт — там; здесь только useMemo + распаковка GenerateSettings в
 * нейтральный ModelCostSettings shape. Та же функция используется на
 * popular-карточках (HomeTemp / PopularModelsTab), чтобы цены на карточке
 * и на /generate не разъезжались.
 */
export function useGenerateCost({
	selectedModel,
	settings,
	requiresVideo,
	selectedVideoDuration,
	uploadedVideoDuration,
	audioOperation = 'generate',
	presentationEstimate,
	hasImage = false,
	hasVideo = false,
	imageCount = 0,
	referenceVideos,
	inputVideoSeconds = null,
}: UseCostParams): number {
	// Длина под замком — та же, что в пилюле и в запросе (durationLock): по видео-референсу, по видео тренда или замок тренда.
	const lockedSec = durationLock(selectedModel, settings, referenceVideos ?? [], requiresVideo && !!uploadedVideoDuration)?.sec
	const duration = lockedSec != null ? String(lockedSec) : settings.duration
	return useMemo(
		() =>
			computeModelCost(
				selectedModel,
				{
					resolution: settings.resolution,
					duration,
					sound: settings.sound,
					speed: settings.speed,
					quality: settings.quality,
					// Уплощаем — useMemo deps учитывает gammaNumCards напрямую,
					// не весь settings.presentation объект (иначе exhaustive-deps ругается).
					presentation: {
						gammaNumCards: settings.presentation?.gammaNumCards,
						gammaImageModel: settings.presentation?.gammaImageModel,
						gammaImageSource: settings.presentation?.gammaImageSource,
					},
				},
				{
					requiresVideo,
					selectedVideoDuration,
					uploadedVideoDuration,
					audioOperation,
					audioSubmode: settings.audioSubmode,
					presentationEstimate,
					hasImage,
					hasVideo,
					imageCount,
					inputVideoSeconds,
				},
			),
		[
			selectedModel,
			duration,
			settings.sound,
			settings.resolution,
			settings.speed,
			settings.quality,
			selectedVideoDuration,
			uploadedVideoDuration,
			requiresVideo,
			audioOperation,
			settings.audioSubmode,
			presentationEstimate,
			settings.presentation?.gammaNumCards,
			settings.presentation?.gammaImageModel,
			settings.presentation?.gammaImageSource,
			hasImage,
			hasVideo,
			imageCount,
			inputVideoSeconds,
		],
	)
}
